"use client";

import { useRef } from "react";

import { useIntersectedOnce } from "@/hooks/use-intersection-once";
import { cn } from "@/lib/utils";

interface YouTubeProps {
  /** react-player v2 prop name — kept for MDX backward compatibility */
  url?: string;
  /** react-player v3 prop name */
  src?: string;
  title?: string;
  className?: string;
}

function extractYouTubeVideoId(videoUrl: string): string | null {
  return (
    videoUrl.match(
      /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([^&\s?]+)/
    )?.[1] ?? null
  );
}

export function YouTube({
  url,
  src,
  title = "YouTube video",
  className,
}: YouTubeProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const inView = useIntersectedOnce(containerRef, {
    root: null,
    rootMargin: "200px",
    threshold: 0,
  });

  const videoUrl = src ?? url;
  const videoId = videoUrl ? extractYouTubeVideoId(videoUrl) : null;

  if (!videoId) {
    return (
      <div className={cn("relative pt-[56.25%]", className)}>
        <div className="bg-grey-100 text-grey-600 absolute inset-0 flex items-center justify-center rounded-lg text-sm">
          Invalid YouTube URL
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={cn("relative pt-[56.25%]", className)}>
      {inView ? (
        <iframe
          src={`https://www.youtube.com/embed/${videoId}`}
          title={title}
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          // YouTube embeds require allow-same-origin alongside allow-scripts to
          // initialise the player; safe here because the iframe is cross-origin.
          // oxlint-disable-next-line react/iframe-missing-sandbox
          sandbox="allow-scripts allow-same-origin allow-presentation allow-popups"
          allowFullScreen
          loading="lazy"
          className="absolute top-0 left-0 size-full"
        />
      ) : (
        <div
          className="bg-grey-100 absolute inset-0 rounded-lg"
          aria-hidden="true"
        />
      )}
    </div>
  );
}
